import type { Bi } from './sectors'

export const sectorsHero = {
  eyebrow: { fr: 'Secteurs', en: 'Sectors' },
  headline: [
    { text: { fr: 'Des écosystèmes différents.', en: 'Different ecosystems.' }, accent: false },
    { text: { fr: 'Une même complexité à connecter.', en: 'The same complexity to untangle.' }, accent: true },
  ],
  body: {
    fr: 'BCITI | ATNA est conçu pour les organisations qui doivent relier des personnes, leurs relations et leur contexte à plusieurs services, communications et opérations.',
    en: 'BCITI | ATNA is built for organizations that need to connect people, their relationships and their context to many services, communications and operations.',
  },
  flow: [
    { key: 'people', label: { fr: 'Personnes', en: 'People' } },
    { key: 'identity', label: { fr: 'Identité et contexte', en: 'Identity & context' } },
    { key: 'services', label: { fr: 'Services et relations', en: 'Services & relationships' } },
    { key: 'atna', label: { fr: 'ATNA', en: 'ATNA' } },
    { key: 'actions', label: { fr: 'Actions', en: 'Actions' } },
  ] as { key: 'people' | 'identity' | 'services' | 'atna' | 'actions'; label: Bi }[],
  signature: [
    { fr: 'Une identité.', en: 'One identity.' },
    { fr: 'Plusieurs relations.', en: 'Many relationships.' },
    { fr: 'Une multitude de services.', en: 'A multitude of services.' },
    { fr: 'Un écosystème à orchestrer.', en: 'One ecosystem to orchestrate.' },
  ],
}

export const sectorsEcosystems = {
  eyebrow: { fr: 'Les écosystèmes', en: 'The ecosystems' },
  headline: [
    { text: { fr: 'Une même fondation.', en: 'One foundation.' }, accent: false },
    { text: { fr: 'Des réalités différentes.', en: 'Different realities.' }, accent: true },
  ],
  // Approved Brossard production proof already used across the site — do not add new figures.
  proof: [
    { value: { fr: '88 266', en: '88,266' }, label: { fr: 'usagers actifs à Brossard', en: 'active users in Brossard' } },
    { value: { fr: 'Depuis 2017', en: 'Since 2017' }, label: { fr: 'en production', en: 'in production' } },
    { value: { fr: 'Plus de 20', en: '20+' }, label: { fr: 'communautés', en: 'communities' } },
  ] as { value: string | Bi; label: Bi }[],
  proofCaption: { fr: 'Ville de Brossard', en: 'City of Brossard' },
}

export const sectorsFoundation = {
  eyebrow: { fr: 'Une fondation commune', en: 'A shared foundation' },
  headline: [
    { text: { fr: 'Ce qui change,', en: 'What changes' }, accent: false },
    { text: { fr: 'c’est l’écosystème.', en: 'is the ecosystem.' }, accent: false },
    { text: { fr: 'Pas la fondation.', en: 'Not the foundation.' }, accent: true },
  ],
  body: {
    fr: 'Derrière chaque écosystème se cache le même besoin : savoir qui interagit, comprendre son contexte et lui donner accès aux bons services — simplement et en confiance.',
    en: 'Behind every ecosystem lies the same need: knowing who is interacting, understanding their context and giving them access to the right services — simply and securely.',
  },
  stages: [
    {
      key: 'identity',
      label: { fr: 'Identité de confiance', en: 'Trusted identity' },
      text: { fr: 'Qui est cette personne ?', en: 'Who is this person?' },
    },
    {
      key: 'relations',
      label: { fr: 'Relations et contexte', en: 'Relationships & context' },
      text: {
        fr: 'Citoyen · client · étudiant · propriétaire · employé · partenaire',
        en: 'Resident · client · student · owner · employee · partner',
      },
    },
    {
      key: 'card',
      label: { fr: 'Carte et identité numérique', en: 'Card & digital identity' },
      text: { fr: 'Une identité pour accéder à l’écosystème', en: 'One identity to access the ecosystem' },
    },
    {
      key: 'services',
      label: { fr: 'Services et transactions', en: 'Services & transactions' },
      text: {
        fr: 'Accéder · demander · payer · réserver · communiquer',
        en: 'Access · request · pay · book · communicate',
      },
    },
    {
      key: 'atna',
      label: { fr: 'Intelligence ATNA', en: 'ATNA intelligence' },
      text: {
        fr: 'Comprendre · vérifier · recommander · orchestrer · agir',
        en: 'Understand · verify · recommend · orchestrate · act',
      },
    },
  ] as { key: 'identity' | 'relations' | 'card' | 'services' | 'atna'; label: Bi; text: Bi }[],
}

export const sectorsCta = {
  headline: [
    { text: { fr: 'Vous gérez un', en: 'Managing a' }, accent: false },
    { text: { fr: 'écosystème complexe ?', en: 'complex ecosystem?' }, accent: false },
    { text: { fr: 'Parlons de ce que', en: 'Let’s talk about what' }, accent: true },
    { text: { fr: 'vous pourriez connecter.', en: 'you could connect.' }, accent: true },
  ],
  body: {
    fr: 'Identité, relations, services, communications, transactions et intelligence — découvrez comment BCITI | ATNA peut devenir la fondation de votre écosystème numérique.',
    en: 'Identity, relationships, services, communications, transactions and intelligence — discover how BCITI | ATNA can become the foundation of your digital ecosystem.',
  },
  cta: { fr: 'Demander une démo', en: 'Request a demo' },
}
